/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/Icon';
import { ROLE_PRESETS, presetFor } from '@/lib/roles';
import { useCanSwitchRoles } from '@/lib/userRole';
import { useRoleView } from '@/stores/roleView';
import { Menu } from './Menu';

/** Header "view as role" switcher for admins previewing narrower personas. */
export function RoleViewMenu({
  open,
  onOpenChange,
  handoffFrom,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The stand-in that opened the menu while its own trigger is hidden (see `Menu`). */
  handoffFrom?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const canSwitch = useCanSwitchRoles();
  const presetId = useRoleView((s) => s.presetId);
  const setPreset = useRoleView((s) => s.setPreset);
  const clear = useRoleView((s) => s.clear);

  // Nothing to offer for users without elevated access — hide the control entirely.
  if (!canSwitch) return null;

  // "All roles" is the default reset, separate from preview personas.
  const active = presetFor(presetId);
  const activeLabel = active ? t(active.labelKey) : t('roles.switcher.allRoles.label');
  const title = t('roles.switcher.title');

  const select = (id: string) => {
    onOpenChange(false);
    setPreset(id);
  };
  const reset = () => {
    onOpenChange(false);
    clear();
  };

  return (
    <Menu
      open={open}
      onOpenChange={onOpenChange}
      handoffFrom={handoffFrom}
      trigger={({ open: isOpen, toggle }) => (
        <button
          type="button"
          className={`tb-lang rv-trigger${presetId ? ' rv-on' : ''}`}
          title={`${title}: ${activeLabel}`}
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={`${title}: ${activeLabel}`}
          onClick={toggle}
        >
          <Icon name="eye" size={18} />
          {presetId && <span className="tb-lang-code">{activeLabel}</span>}
        </button>
      )}
    >
      <div className="lang-head">
        <span className="lang-head-icon">
          <Icon name="eye" size={20} />
        </span>
        <div className="lang-head-text">
          <div className="lang-head-title">{title}</div>
          <div className="lang-head-current">{activeLabel}</div>
        </div>
      </div>
      <p className="rv-hint">{t('roles.switcher.hint')}</p>
      <div className="lang-list" role="group" aria-label={title}>
        {ROLE_PRESETS.map((p) => {
          const selected = presetId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              aria-current={selected ? 'true' : undefined}
              className={`lang-item${selected ? ' on' : ''}`}
              onClick={() => select(p.id)}
            >
              <span className="lang-item-flag">
                <Icon name="user" size={16} />
              </span>
              <span className="lang-item-text">
                <span className="lang-item-name">{t(p.labelKey)}</span>
                <span className="lang-item-sub">{t(p.descriptionKey)}</span>
              </span>
              {selected && (
                <span className="lang-item-check">
                  <Icon name="check" size={16} />
                </span>
              )}
            </button>
          );
        })}
        <div className="rv-sep" />
        <button
          type="button"
          aria-current={presetId === null ? 'true' : undefined}
          className={`lang-item${presetId === null ? ' on' : ''}`}
          onClick={reset}
        >
          <span className="lang-item-flag">
            <Icon name="home" size={16} />
          </span>
          <span className="lang-item-text">
            <span className="lang-item-name">{t('roles.switcher.allRoles.label')}</span>
            <span className="lang-item-sub">{t('roles.switcher.allRoles.description')}</span>
          </span>
          {presetId === null && (
            <span className="lang-item-check">
              <Icon name="check" size={16} />
            </span>
          )}
        </button>
      </div>
    </Menu>
  );
}
